import * as React from 'react';
import { useState } from 'react';
import { Button, Input, Popover, Tag, message } from 'antd';
import { CloseOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { ResolutionKnowledgeFeedback } from '../../types/resolutionKnowledge';

/** Stored as free-form `tags` on the feedback record. */
const FEEDBACK_REASONS = [
	{ tag: 'not_useful', label: 'Not useful' },
	{ tag: 'other', label: 'Other' },
];

const SAVE_ERROR_MESSAGE = 'Could not save your feedback. Please try again.';

interface ResolutionFeedbackControlsProps {
	/** The caller's saved feedback, if any. */
	feedback: ResolutionKnowledgeFeedback | undefined;
	/** Show the "Did you find this useful?" prompt. */
	showPrompt: boolean;
	saveFeedback: (feedback: ResolutionKnowledgeFeedback) => Promise<any>;
	onDismiss: () => void;
}

export const ResolutionFeedbackControls = ({
	feedback,
	showPrompt,
	saveFeedback,
	onDismiss,
}: ResolutionFeedbackControlsProps) => {
	const [reasonFormOpen, setReasonFormOpen] = useState(false);
	const [selectedTag, setSelectedTag] = useState<string>(null);
	const [note, setNote] = useState('');
	// One feedback PUT at a time: each replaces the whole record, so a rating save finishing
	// after a reason save would wipe the reason. The controls are disabled while one is in flight.
	const [saving, setSaving] = useState(false);

	const isPositive = !!feedback && feedback.isPositive;
	const isNegative = !!feedback && !feedback.isPositive;

	const showSaveError = () => message.error(SAVE_ERROR_MESSAGE);

	const save = (next: ResolutionKnowledgeFeedback) => {
		setSaving(true);
		return saveFeedback(next).finally(() => setSaving(false));
	};

	const resetReasonForm = () => {
		setReasonFormOpen(false);
		setSelectedTag(null);
		setNote('');
	};

	const rateHelpful = () => {
		if (isPositive) return;
		resetReasonForm();
		save({ isPositive: true }).catch(showSaveError);
	};

	// The rating is saved straight away so skipping the follow-up form still records it.
	const rateNotHelpful = () => {
		if (isNegative) {
			setReasonFormOpen(true);
			return;
		}
		setReasonFormOpen(true);
		save({ isPositive: false }).catch(showSaveError);
	};

	const handleReasonFormVisibleChange = (visible: boolean) => {
		if (!visible) resetReasonForm();
	};

	const toggleTag = (tag: string) => setSelectedTag(selectedTag === tag ? null : tag);

	const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
		setNote(event.target.value);

	const submitReason = () => {
		const reason = note.trim();
		save({
			isPositive: false,
			...(reason ? { reason } : {}),
			...(selectedTag ? { tags: [selectedTag] } : {}),
		})
			.then(() => {
				resetReasonForm();
				onDismiss();
			})
			.catch(showSaveError);
	};

	const reasonForm = (
		<div className="flex w-80 flex-col gap-s">
			<span className="bodybold text-character-primary">What did we get wrong?</span>
			<div className="flex flex-wrap gap-xs">
				{FEEDBACK_REASONS.map((option) => (
					<ReasonChip
						key={option.tag}
						tag={option.tag}
						label={option.label}
						selected={selectedTag === option.tag}
						onToggle={toggleTag}
					/>
				))}
			</div>
			<Input.TextArea
				rows={3}
				placeholder="Tell us more (optional)"
				value={note}
				onChange={handleNoteChange}
			/>
			<div className="flex justify-end gap-xs">
				<Button onClick={resetReasonForm}>Skip</Button>
				<OWButton type="primary" loading={saving} onClick={submitReason}>
					Submit
				</OWButton>
			</div>
		</div>
	);

	return (
		<div className="flex shrink-0 items-center gap-xs">
			{showPrompt ? (
				<span className="bodyregular whitespace-nowrap text-character-primary">
					Did you find this useful?
				</span>
			) : null}
			<OWButton
				size="small"
				title="Helpful"
				aria-pressed={isPositive}
				type={isPositive ? 'primary' : 'default'}
				icon={<LikeOutlined />}
				disabled={saving}
				onClick={rateHelpful}
			/>
			<Popover
				trigger="click"
				placement="bottomRight"
				visible={reasonFormOpen}
				onVisibleChange={handleReasonFormVisibleChange}
				content={reasonForm}
			>
				<OWButton
					size="small"
					title="Not helpful"
					aria-pressed={isNegative}
					type={isNegative ? 'primary' : 'default'}
					icon={<DislikeOutlined />}
					disabled={saving}
					onClick={rateNotHelpful}
				/>
			</Popover>
			<Button
				size="small"
				type="text"
				title="Dismiss"
				className="text-character-secondary"
				icon={<CloseOutlined />}
				onClick={onDismiss}
			/>
		</div>
	);
};

interface ReasonChipProps {
	tag: string;
	label: string;
	selected: boolean;
	onToggle: (tag: string) => void;
}

const ReasonChip = ({ tag, label, selected, onToggle }: ReasonChipProps) => {
	const handleChange = () => onToggle(tag);
	return (
		<Tag.CheckableTag checked={selected} onChange={handleChange}>
			{label}
		</Tag.CheckableTag>
	);
};
